/**
 * Pure view-model logic for a pipeline's coding batches.
 *
 * Kept free of React — like `recordCodings.ts` and `llmLanesModel.ts` — so the
 * rules that are actually rules (what a batch cost, where it is in the phase
 * machine, whether the page is still moving) can be tested directly.
 *
 * THE RULE: nothing here invents a number. A batch that never ran has no token
 * answer (null, not 0), and a batch the old drain owned has no phase machine to
 * draw (an empty stepper, not a fabricated "initialized").
 */
import type { CodingBatchSummary } from '../types/codingBatches'

/**
 * What a batch cost, from whatever the provider reported.
 *
 * `total_tokens` wins when present — it is the provider's own arithmetic.
 * Otherwise prompt + completion. `cached_tokens` and `reasoning_tokens` are
 * SUBSETS of those two everywhere we bill, so adding them would double count.
 *
 * null means "no usage recorded", which is a different statement from zero.
 */
export const totalTokens = (usage?: Record<string, number> | null): number | null => {
  if (!usage) return null
  const num = (key: string): number | null => {
    const v = usage[key]
    return typeof v === 'number' && Number.isFinite(v) ? v : null
  }
  const total = num('total_tokens')
  if (total !== null) return total
  const prompt = num('prompt_tokens')
  const completion = num('completion_tokens')
  if (prompt === null && completion === null) return null
  return (prompt ?? 0) + (completion ?? 0)
}

/** The multi-phase machine: a framework with looped variables. */
export const MULTI_PHASE_STEPS = ['initialized', 'extracting', 'classifying', 'stitching', 'completed']

/** The slim machine: no looped variables anywhere. */
export const SLIM_PHASE_STEPS = ['initialized', 'coding', 'completed']

export interface PhaseStep {
  phase: string
  label: string
  /** The phase the batch is in right now. */
  active: boolean
  /** A phase the batch has already left. */
  done: boolean
  /** The batch stopped here. */
  error?: boolean
}

const PHASE_LABELS: Record<string, string> = {
  initialized: 'Initialized',
  coding: 'Coding',
  extracting: 'Extracting',
  classifying: 'Classifying',
  stitching: 'Stitching',
  completed: 'Completed',
  failed: 'Failed',
}

const phaseLabel = (phase: string): string => PHASE_LABELS[phase] ?? phase

/**
 * The batch's progress through its phase machine.
 *
 * Which machine it is showing is inferred from the phase the batch is IN —
 * `coding` is only ever reached by the slim shape, everything else by the
 * multi-phase one — because the batch row does not record which plan it ran.
 * `completed` is ambiguous by that rule and lands on the multi-phase machine,
 * every step done, which reads correctly either way.
 *
 * A `failed` batch keeps whatever phase it stopped in, so it is drawn as the
 * multi-phase machine with a failed step appended: the honest reading is "it
 * got somewhere and then stopped", not "it reached stitching".
 *
 * A legacy (phaseless) batch returns NO steps — the old in-process drain had no
 * machine, and drawing one would be an invention.
 */
export const phaseStepper = (phase?: string | null): PhaseStep[] => {
  if (!phase) return []

  if (phase === 'failed') {
    return [...MULTI_PHASE_STEPS.slice(0, -1), 'failed'].map((p, i, all) => ({
      phase: p,
      label: phaseLabel(p),
      active: i === all.length - 1,
      done: false,
      ...(p === 'failed' ? { error: true } : {}),
    }))
  }

  const steps = phase === 'coding' ? SLIM_PHASE_STEPS : MULTI_PHASE_STEPS
  const index = steps.indexOf(phase)
  return steps.map((p, i) => ({
    phase: p,
    label: phaseLabel(p),
    active: i === index && phase !== 'completed',
    done: phase === 'completed' || (index >= 0 && i < index),
  }))
}

/** States that mean the queue is still working on this batch. */
const ACTIVE_STATES = new Set(['pending', 'running'])

/**
 * Whether anything on the page is still moving — the polling gate. A settled
 * page of batches is history and must not be re-fetched every five seconds.
 */
export const batchesHaveActiveRows = (batches: CodingBatchSummary[]): boolean =>
  batches.some(b => ACTIVE_STATES.has(b.state))

/**
 * How a batch's campaign label should READ, which is not the same as what the
 * label says.
 *
 * Three distinct answers, and collapsing any two of them would be a lie:
 *
 *   - a campaign id → that campaign paid for this batch;
 *   - `unknown` → OUR gap. We could not attribute it, so its tokens are billed
 *     to no product and appear on no product page. Since C4 the Sprinklr sender
 *     refuses to create these at all, so one on screen is a real signal —
 *     either a pre-C4 batch or a sender that stopped honouring the rule;
 *   - no label at all → a batch from before labels existed. Settled history,
 *     honestly unattributed, and NOT the same as `unknown`.
 *
 * `none` is left to render as itself: it means the source told us there is no
 * campaign, which is knowledge, not a gap.
 */
export type CampaignAttribution =
  | { kind: 'campaign'; value: string }
  | { kind: 'unknown' }
  | { kind: 'unlabelled' }

/** The campaign key every submitter stamps. Mirrors campaign.LabelKey in Go. */
export const CAMPAIGN_LABEL_KEY = 'campaign_id'

/** Mirrors campaign.Unknown in Go — our own gap, not a value from the source. */
export const CAMPAIGN_UNKNOWN = 'unknown'

export const campaignAttribution = (
  labels?: Record<string, string> | null,
): CampaignAttribution => {
  const value = labels?.[CAMPAIGN_LABEL_KEY]
  if (!value) return { kind: 'unlabelled' }
  if (value === CAMPAIGN_UNKNOWN) return { kind: 'unknown' }
  return { kind: 'campaign', value }
}

export type BatchStateColor = 'default' | 'info' | 'success' | 'error'

/**
 * Chip colour for a batch state. An unrecognised state renders neutral rather
 * than being coerced into one of ours — a new queue state should look unfamiliar,
 * not quietly green.
 */
export const batchStateColor = (state: string): BatchStateColor => {
  switch (state) {
    case 'completed':
      return 'success'
    case 'failed':
      return 'error'
    case 'running':
      return 'info'
    default:
      return 'default'
  }
}

/** Is the batch queued but held back because its billed team's AI budget is used, or it has none? */
export const isHeld = (batch: { held_reason?: string }): boolean => !!batch.held_reason

/** Tooltip for the Held chip: the server's reason plus what happens next. */
export const heldTooltip = (batch: { held_reason?: string }): string => {
  // The server's reason has no full stop ("Held: the X team's AI budget is used").
  const reason = (batch.held_reason ?? '').trim().replace(/\.?$/, '')
  return reason ? `${reason}. It starts by itself once the team has budget.` : 'It starts by itself once the team has budget.'
}

/**
 * The reason a held batch carries for a team whose budget is used. The server
 * builds the same words (goapi/pkg/autocoder/batch_controller.go
 * budgetHoldReason); change both together. The billing simulator's Warnings
 * view uses this so it can show the Held chip's tooltip for a fictional team.
 */
export const budgetUsedHeldReason = (team: string): string =>
  team ? `Held: the ${team} team's AI budget is used` : "Held: the team's AI budget is used"
